import { Injectable } from '@angular/core';

import { NavigationService } from '@core/services/navigation/navigation.service';
import { ProductsService } from '@core/services/products/products.service';
import { UserDataService } from '@core/services/user-data/user-data.service';
import { BaseProductCommand } from '@shared/commands/base-product.command';
import { IProductCommandContext } from '@shared/models/command';
import { ProductTypes } from '@shared/models/products.model';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

/**
 * Команда для выполнения платежей и переводов
 *
 * Эта команда предоставляет возможность пользователю инициировать платежи и переводы
 * с различных типов банковских продуктов. Поддерживает работу со счетами, картами,
 * депозитами и накопительными счетами.
 *
 * Основные функции:
 * - Навигация на страницу платежей с предварительным выбором продукта
 * - Контекстное отображение заголовка в зависимости от типа продукта
 * - Проверка возможности выполнения платежных операций для конкретного продукта
 *
 * @example
 * ```typescript
 * // Использование в контексте продукта
 * const context = { data: { id: 'account-123', type: ProductTypes.ACCOUNT } };
 * await paymentCommand.execute(context);
 * // Пользователь будет перенаправлен на страницу платежей с выбранным счетом
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PaymentOrTransferCommand extends BaseProductCommand {
  /** Мапа заголовков для разных типов продуктов */
  private readonly titleMap = {
    [ProductTypes.CARD]: 'Оплатить или перевести',
    [ProductTypes.ACCOUNT]: 'Платеж или перевод',
    [ProductTypes.DEPOSIT]: 'Платеж или перевод',
    [ProductTypes.SAVE_ACCOUNT]: 'Платеж или перевод'
  } as const;

  /** Уникальный идентификатор типа команды */
  readonly type = 'paymentOrTransfer';

  /** Иконка команды в интерфейсе */
  readonly icon = 'forward';

  /** Типы продуктов, для которых доступна команда */
  readonly productType: ProductTypes[] = [
    ProductTypes.ACCOUNT,
    ProductTypes.CARD,
    ProductTypes.DEPOSIT,
    ProductTypes.SAVE_ACCOUNT
  ];

  /** Флаг, указывающий что это не информационное действие */
  readonly isInfoAction = false;

  /** Настройки системы для проверки доступности команды */
  readonly settingsNames = ['PAY/SELF_FREE', 'PAY/INVOICE', 'PAY/TRANSFER_RUR', 'PAY/SERVICE'];

  /** Порядок отображения в списке действий */
  readonly order = 4; // Четвертый в actions для account и deposit

  /** Заголовок по умолчанию */
  readonly fallbackTitle = 'Платеж или перевод';

  /**
   * Возвращает контекстный заголовок команды
   * Заголовок изменяется в зависимости от типа продукта и контекста выполнения
   */
  get title(): string {
    return this.getContextualTitle(this.titleMap, this.fallbackTitle);
  }

  constructor(
    private navigationService: NavigationService,
    private userDataService: UserDataService,
    private productsService: ProductsService,
    commandAvailabilityService: CommandAvailabilityService
  ) {
    super(commandAvailabilityService);
  }

  /**
   * Выполняет команду платежа или перевода
   *
   * Устанавливает выбранный продукт в UserDataService и осуществляет навигацию
   * на страницу платежей. Контекст автоматически устанавливается CommandService.
   *
   * @param context - контекст команды с информацией о продукте
   */
  async execute(context: IProductCommandContext): Promise<void> {
    this.userDataService.selectedProduct = context.data.id;
    await this.navigationService.push('/payments');
  }

  /**
   * Проверяет дополнительную доступность команды для конкретного продукта
   *
   * Воспроизводит точную логику из products-original.service.ts (строки 846-853):
   * - Проверка выполняется ТОЛЬКО для типов: ['account', 'card', 'deposit']
   * - Для накопительных счетов (saveAccount) проверка НЕ выполняется
   * - Это позволяет накопительным счетам с любой валютой (включая Йены) иметь команду
   *
   * @param context - контекст команды с информацией о продукте
   * @returns true если платежи доступны для данного продукта
   */
  protected checkAdditionalAvailability(context: IProductCommandContext): boolean {
    const product = context.data;

    // Воспроизводим оригинальную логику из products-original.service.ts:
    // if (includes(['account', 'card', 'deposit'], product.productType) && !this.checkPaymentOrTransferPossibility(product))
    // saveAccount НЕ входит в этот список, поэтому для него проверка не выполняется
    const typesToCheck = ['account', 'card', 'deposit'];

    if (typesToCheck.includes(product.productType)) {
      return this.productsService.checkPaymentOrTransferPossibility(product);
    }

    // Для saveAccount и других типов - команда всегда доступна (как в старом коде)
    return true;
  }
}
